import PropTypes from 'prop-types';
import styled from 'styled-components';
import Link from 'next/link';

import { config } from '_config';
import { theme } from '_constants/theme';

import * as Layout from '_components/atomic/layouts';
import JoinBanner from '../JoinBanner';

export const howToWriteABusinessPlanStepByStepGuideContents = [
  'Executive Summary',
  'Business Description',
  'Market Analysis',
  'Financial Projections',
];

const StyledContainer = styled.div`
  position: relative;
`;

const StyledBlock = styled.div`
  position: relative;
  z-index: 1;

  ul {
    padding-top: ${theme.spaces.regular}px;

    li {
      margin-bottom: ${theme.spaces.regular}px;

      b {
        text-decoration: underline;
      }
    }
  }
`;

const StyledBackgroundImageContainer = styled.div`
  position: absolute;
  top: 5%;
  width: 100%;

  img {
    width: 100%;
  }
`;

const HowToWriteABusinessPlanStepByStepGuideArticle = ({ idPrefix }) => {
  return (
    <StyledContainer>
      <Layout.Card.Text>
        A well-crafted business plan is essential for guiding the growth and
        success of your business. Whether you&apos;re starting a new venture or
        looking to expand an existing one, a business plan serves as a roadmap
        for your journey. In this comprehensive guide, we&apos;ll walk you
        through the process of writing a business plan, step by step.
      </Layout.Card.Text>
      <StyledBlock id={`${idPrefix}-${0}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Executive Summary
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          What is an Executive Summary?
        </Layout.Card.Text>
        <Layout.Card.Text>
          The executive summary is a brief overview of your business plan,
          highlighting key points such as your business idea, target market, and
          financial projections.
          <br />
          How to Write an Executive Summary:
          <ul>
            <li>
              Start with a compelling opening statement that summarizes your
              business idea.
            </li>
            <li>Outline your business goals and objectives.</li>
            <li>Provide an overview of your target market and competition.</li>
            <li>
              Summarize your financial projections and funding requirements.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${1}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Business Description
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          What is a Business Description?
        </Layout.Card.Text>
        <Layout.Card.Text>
          The business description section provides an overview of your
          business, including its mission statement, history, and legal
          structure.
          <br />
          How to Write a Business Description:
          <ul>
            <li>Describe your business idea and the problem it solves.</li>
            <li>
              Explain your unique selling proposition (USP) and what sets your
              business apart from competitors.
            </li>
            <li>
              Provide details about your target market and how you plan to reach
              them.
            </li>
            <li>
              Outline your business&apos;s legal structure, ownership, and
              management team.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${2}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Market Analysis
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          What is a Market Analysis?
        </Layout.Card.Text>
        <Layout.Card.Text>
          The market analysis section examines the industry landscape, target
          market, and competition to identify opportunities and challenges.
          <br />
          How to Conduct a Market Analysis:
          <ul>
            <li>
              Research your industry, including market size, trends, and growth
              potential.
            </li>
            <li>Identify your target market and customer demographics.</li>
            <li>
              Analyze your competitors, their strengths, weaknesses, and market
              share.
            </li>
            <li>
              Use SWOT analysis (Strengths, Weaknesses, Opportunities, Threats)
              to assess your business&apos;s position in the market.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${3}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Financial Projections
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          What are Financial Projections?
        </Layout.Card.Text>
        <Layout.Card.Text>
          Financial projections provide an estimate of your business&apos;s
          future financial performance, including revenue, expenses, and
          profits.
          <br />
          Creating Financial Projections:
          <ul>
            <li>
              Prepare a sales forecast based on market research and sales
              projections.
            </li>
            <li>Estimate your startup costs and operating expenses.</li>
            <li>
              Develop a cash flow projection to ensure you have enough capital
              to cover expenses.
            </li>
            <li>
              Create a profit and loss statement (P&L) and balance sheet to
              assess your business&apos;s financial health.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Card.Text>
        Writing a business plan is a crucial step in launching and growing a
        successful business. By following this step-by-step guide, you can
        create a comprehensive business plan that serves as a roadmap for your
        business&apos;s success.
      </Layout.Card.Text>
      <Layout.Card.Text>
        For expert guidance on writing a business plan, visit&nbsp;
        <Link href="/">Incorpified</Link>. Our team of business consultants can
        help you develop a customized business plan tailored to your unique
        business needs.
      </Layout.Card.Text>
      <JoinBanner />
      <StyledBackgroundImageContainer>
        <picture>
          <source
            srcSet={`${config.basePath}/images/blog-arrow-logo2.svg`}
            type="image/svg+xml"
          />
          <img
            src={`${config.basePath}/images/blog-arrow-logo2.svg`}
            alt="Background Logo"
          />
        </picture>
      </StyledBackgroundImageContainer>
    </StyledContainer>
  );
};

HowToWriteABusinessPlanStepByStepGuideArticle.propTypes = {
  idPrefix: PropTypes.string,
};

export default HowToWriteABusinessPlanStepByStepGuideArticle;
